import type { TeamMember, CreateTeamMemberDto, UpdateTeamMemberDto, User } from '@/lib/sdk/team/types';

export interface ParsedUser {
  id?: string;
  name: string;
  country_code: string;
  phone_number: string;
  chatbot_id?: string;
  org_code?: string; // Optional field for organization code
  rowNumber: number;
}

export interface CSVValidationError {
  row: number;
  field: string;
  value: string;
  error: string;
}

export interface CSVParseResult {
  users: ParsedUser[];
  errors: CSVValidationError[];
  newUsers: ParsedUser[];
  existingUsers: ParsedUser[];
}

/**
 * Export users to CSV format
 */
export const exportUsersToCSV = (users: TeamMember[], filename = 'users.csv'): void => {
  const headers = ['id', 'name', 'country_code', 'phone_number', 'org_code'];
  
  const csvContent = [
    headers.join(','),
    ...users.map(user => [
      user.id,
      `"${user.name.replace(/"/g, '""')}"`, // Escape quotes
      user.country_code,
      user.phone_number.replace(user.country_code, ''), // Remove country code from phone number
      user.org_code || '' // Include org_code if available
    ].join(','))
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Normalize a CSV country_code value to always be '+'-prefixed
 * (e.g. "91" -> "+91"), so users don't have to remember to type the '+'.
 */
const normalizeCountryCode = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return trimmed.startsWith('+') ? trimmed : `+${trimmed}`;
};

/**
 * Parse CSV content into users array
 */
export const parseCSVContent = (csvText: string): ParsedUser[] => {
  const lines = csvText.trim().split('\n');
  
  if (lines.length === 0) {
    throw new Error('CSV file is empty');
  }

  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  
  // Validate headers
  const requiredHeaders = ['id', 'name', 'country_code', 'phone_number','org_code'];
  const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
  
  if (missingHeaders.length > 0) {
    throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
  }

  const users: ParsedUser[] = [];
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines
    
    const values = parseCSVLine(line);
    
    if (values.length < headers.length) {
      continue; // Skip incomplete rows
    }

    const user: ParsedUser = {
      rowNumber: i + 1,
      id: values[headers.indexOf('id')]?.trim() || undefined,
      name: values[headers.indexOf('name')]?.replace(/^"|"$/g, '').replace(/""/g, '"').trim() || '',
      country_code: normalizeCountryCode(values[headers.indexOf('country_code')] || ''),
      phone_number: values[headers.indexOf('phone_number')]?.trim() || '',
      org_code: values[headers.indexOf('org_code')]?.trim() || '',
    };

    // Only add users with at least a name
    if (user.name) {
      users.push(user);
    }
  }

  return users;
};

/**
 * Parse a single CSV line handling quoted values
 */
const parseCSVLine = (line: string): string[] => {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      values.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  // Add the last field
  values.push(current);
  
  return values;
};

/**
 * Validate parsed users
 */
export const validateUsers = (users: ParsedUser[]): CSVValidationError[] => {
  const errors: CSVValidationError[] = [];
  const phoneNumbers = new Set<string>();

  users.forEach(user => {
    // Validate name
    if (!user.name || user.name.trim().length === 0) {
      errors.push({
        row: user.rowNumber,
        field: 'name',
        value: user.name,
        error: 'Name is required'
      });
    }

    // Validate country code
    if (!user.country_code || !user.country_code.startsWith('+')) {
      errors.push({
        row: user.rowNumber,
        field: 'country_code',
        value: user.country_code,
        error: 'Country code must start with +'
      });
    }

    // Validate phone number
    if (!user.phone_number || !/^\d{6,15}$/.test(user.phone_number)) {
      errors.push({
        row: user.rowNumber,
        field: 'phone_number',
        value: user.phone_number,
        error: 'Phone number must be 6-15 digits'
      });
    }

    // Check for duplicate phone numbers
    const fullPhone = user.country_code + user.phone_number;
    if (phoneNumbers.has(fullPhone)) {
      errors.push({
        row: user.rowNumber,
        field: 'phone_number',
        value: fullPhone,
        error: 'Duplicate phone number found in CSV'
      });
    } else {
      phoneNumbers.add(fullPhone);
    }
  });

  return errors;
};

/**
 * Parse and validate CSV file
 */
export const parseUsersCSV = async (file: File): Promise<CSVParseResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvText = e.target?.result as string;
        const users = parseCSVContent(csvText);
        const errors = validateUsers(users);
        
        const newUsers = users.filter(user => !user.id);
        const existingUsers = users.filter(user => user.id);
        
        resolve({
          users,
          errors,
          newUsers,
          existingUsers
        });
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsText(file);
  });
};

/**
 * Convert parsed users to API DTOs
 */
// CSV rows don't carry a permissions column; the API requires one (User.permissions),
// so new imports get the same 'read_write' default the Add Team Member form uses.
const DEFAULT_IMPORT_PERMISSION = 'read_write';

// The API concatenates country_code + phone_number itself when persisting
// (the Add/Edit Team Member form already sends phone_number raw and relies on
// this). Import must send phone_number raw too — pre-concatenating it here
// like the deprecated page did causes the backend to concatenate on top of
// that again, doubling the country code (e.g. "+91+91...").
export const convertToCreateDto = (users: ParsedUser[], chatbotId?: string): CreateTeamMemberDto => {
  const newUsers: User[] = users
    .filter(user => !user.id) // Only new users
    .map(user => ({
      name: user.name,
      country_code: user.country_code,
      phone_number: user.phone_number,
      permissions: DEFAULT_IMPORT_PERMISSION,
      ...(chatbotId && { chatbot_id: chatbotId })
      , ...(user.org_code && { org_code: user.org_code }) // Include org_code if available
    }));

  return {
    users: newUsers
  };
};

// getExistingPermission looks up an existing member's current permission by id,
// so updates preserve it instead of silently downgrading them to the default.
export const convertToUpdateDto = (
  users: ParsedUser[],
  getExistingPermission?: (id: string) => string | undefined
): UpdateTeamMemberDto => {
  const existingUsers: User[] = users
    .filter(user => user.id) // Only existing users
    .map(user => ({
      id: user.id!,
      name: user.name,
      country_code: user.country_code,
      phone_number: user.phone_number,
      permissions: getExistingPermission?.(user.id!) ?? DEFAULT_IMPORT_PERMISSION
      , ...(user.org_code && { org_code: user.org_code }) // Include org_code if available
    }));

  return {
    users: existingUsers
  };
};